import { useState, useEffect } from "react";
import { collection, getDocs, doc, writeBatch, query, orderBy, limit } from "firebase/firestore";
import { db, storage } from "../../../db/firebase";
import { useNavigate } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// Define a maximum for images
const MAX_IMAGES = 5;

// Simplified upload function using firebase/storage
const uploadFile = async (file, folderPath) => {
  if (!file) return null;
  const storageRef = ref(storage, `${folderPath}/${Date.now()}_${file.name}`);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(snapshot.ref);
  return downloadURL;
};

// Simple helper to create a unique ID for variant keys
const createId = () => Math.random().toString(36).substring(2, 9);

// Custom Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder = "Start writing..." }) => {
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);

  const handleFormat = (format) => {
    const textarea = document.getElementById('rich-text-area');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    let formattedText = '';
    
    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        setIsBold(!isBold);
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        setIsItalic(!isItalic);
        break;
      case 'underline':
        formattedText = `<u>${selectedText}</u>`;
        setIsUnderline(!isUnderline);
        break;
      case 'h1':
        formattedText = `# ${selectedText}`;
        break;
      case 'h2':
        formattedText = `## ${selectedText}`;
        break;
      case 'h3':
        formattedText = `### ${selectedText}`;
        break;
      case 'bullet':
        formattedText = `- ${selectedText}`;
        break;
      case 'number':
        formattedText = `1. ${selectedText}`;
        break;
      default:
        formattedText = selectedText;
    }
    
    const newValue = value.substring(0, start) + formattedText + value.substring(end);
    onChange(newValue);
    
    // Restore focus and selection
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, start + formattedText.length);
    }, 0);
  };

  const ToolbarButton = ({ onClick, children, title }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md text-sm font-medium transition-colors"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="border-b border-gray-300 bg-gray-50 p-3 flex flex-wrap gap-2">
        <div className="flex gap-1">
          <ToolbarButton onClick={() => handleFormat('h1')} title="Heading 1">
            H1
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('h2')} title="Heading 2">
            H2
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('h3')} title="Heading 3">
            H3
          </ToolbarButton>
        </div>
        
        <div className="flex gap-1">
          <ToolbarButton onClick={() => handleFormat('bold')} title="Bold">
            <strong>B</strong>
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('italic')} title="Italic">
            <em>I</em>
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('underline')} title="Underline">
            <u>U</u>
          </ToolbarButton>
        </div>
        
        <div className="flex gap-1">
          <ToolbarButton onClick={() => handleFormat('bullet')} title="Bullet List">
            • List
          </ToolbarButton>
          <ToolbarButton onClick={() => handleFormat('number')} title="Numbered List">
            1. List
          </ToolbarButton>
        </div>
      </div>
      
      {/* Textarea */}
      <textarea
        id="rich-text-area"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-64 p-4 resize-none focus:outline-none text-gray-900"
        style={{ fontFamily: 'monospace', lineHeight: '1.6' }}
      />
    </div>
  );
};

// Markdown Preview Component
const MarkdownPreview = ({ content }) => {
  const renderMarkdown = (text) => {
    if (!text) return <p className="text-gray-500 italic">Start typing to see preview...</p>;
    
    // Basic markdown parsing
    const html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')
      // Underline
      .replace(/<u>(.*?)<\/u>/gim, '<u class="underline">$1</u>')
      // Bullet points
      .replace(/^- (.*$)/gim, '<li class="ml-4 list-disc">$1</li>')
      // Numbered lists
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
      // Line breaks
      .replace(/\n$/gim, '<br>')
      // Paragraphs
      .replace(/\n\n/gim, '</p><p class="my-2">')
      .replace(/([^\n])\n([^\n])/gim, '$1<br>$2');
    
    return `<p class="my-2">${html}</p>`;
  };

  return (
    <div 
      className="prose max-w-none h-64 overflow-y-auto p-4 border border-gray-300 rounded-lg bg-white"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
};

export default function ProductForm() {
  const [form, setForm] = useState({
    PName: "",
    PDesc: "", // Simple description
    PDetailedDesc: "", // NEW: Detailed description with rich text
    PDateAdded: new Date().toISOString().split("T")[0],
    PCode: "",
    PCategories: [], // Array of category IDs
    PType: [{ 
      id: createId(), 
      PTypeName: "Default", 
      PPrice: 0, 
      PSoleColor: "",
      PInOffer: false,
      PDiscountPrice: 0,
      PImgUrls: [],
      PVidUrls: [],
      PStock: [
        {
          id: createId(),
          Qty: 0,
          Size: ""
        }
      ] // Array of stock objects with Qty and Size
    }], // Initial variant/type
    PRating: 0,
    PReviews: [],
    // NEW: Top-level field for efficient querying
    hasActiveOffer: false,
    // NEW: Order field for product ordering
    order: 0
  });

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]); // Fetched category list
  const [organizedCategories, setOrganizedCategories] = useState([]); // NEW: Organized with hierarchy
  const [expandedCategories, setExpandedCategories] = useState(new Set()); // NEW: Track expanded categories
  const [nextOrderNumber, setNextOrderNumber] = useState(1); // NEW: Track next order number

  const navigate = useNavigate();

  // --- Category Fetching and Organization ---
  useEffect(() => {
    const fetchCategories = async () => {
      const snap = await getDocs(collection(db, "categories"));
      const categoriesData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCategories(categoriesData);
      
      // NEW: Organize categories into hierarchy
      const organized = organizeCategoriesHierarchy(categoriesData);
      setOrganizedCategories(organized);
    };
    fetchCategories();
  }, []);

  // NEW: Fetch the highest order number to determine next order
  useEffect(() => {
    const fetchHighestOrder = async () => {
      try {
        const productsQuery = query(
          collection(db, "products"),
          orderBy("order", "desc"),
          limit(1)
        );
        
        const querySnapshot = await getDocs(productsQuery);
        
        if (!querySnapshot.empty) {
          const highestOrderProduct = querySnapshot.docs[0].data();
          setNextOrderNumber((highestOrderProduct.order || 0) + 1);
        } else {
          setNextOrderNumber(1); // Start from 1 if no products exist
        }
      } catch (error) {
        console.error("Error fetching highest order:", error);
        setNextOrderNumber(1); // Fallback to 1
      }
    };

    fetchHighestOrder();
  }, []);

  // NEW: Function to organize categories into hierarchy
  const organizeCategoriesHierarchy = (categoriesData) => {
    const mainCategories = categoriesData.filter(cat => !cat.ParentCategory);
    const subCategories = categoriesData.filter(cat => cat.ParentCategory);

    // Add subcategories to their parent categories
    const categoriesWithChildren = mainCategories.map(mainCat => ({
      ...mainCat,
      subcategories: subCategories.filter(subCat => subCat.ParentCategory === mainCat.id)
    }));

    return categoriesWithChildren;
  };

  // NEW: Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // --- Effect to update hasActiveOffer when variants change ---
  useEffect(() => {
    // Check if any variant has PInOffer set to true
    const hasOffer = form.PType.some(type => type.PInOffer === true);
    
    // Update the top-level field if it's different from current value
    if (form.hasActiveOffer !== hasOffer) {
      setForm(prevForm => ({
        ...prevForm,
        hasActiveOffer: hasOffer
      }));
    }
  }, [form.PType, form.hasActiveOffer]);

  // --- General Form and File Handlers ---

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "PInOffer") {
      setForm({ ...form, [name]: checked });
    } else if (name === "order") {
      // Handle manual order input
      setForm({ ...form, [name]: parseInt(value) || 0 });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  // NEW: Handle rich text editor change for detailed description
  const handleDetailedDescChange = (value) => {
    setForm(prevForm => ({
      ...prevForm,
      PDetailedDesc: value
    }));
  };

  // --- Updated Category Checkbox Handler with Subcategory Support ---
  const handleCategoryChange = (catId, isChecked, isSubcategory = false) => {
    setForm((prevForm) => {
      let newCategories;
      
      if (isChecked) {
        // Add category to the list
        newCategories = [...prevForm.PCategories, catId];
      } else {
        // Remove category from the list
        newCategories = prevForm.PCategories.filter(id => id !== catId);
        
        // NEW: If removing a main category, also remove all its subcategories
        if (!isSubcategory) {
          const mainCategory = organizedCategories.find(cat => cat.id === catId);
          if (mainCategory && mainCategory.subcategories) {
            const subcategoryIds = mainCategory.subcategories.map(sub => sub.id);
            newCategories = newCategories.filter(id => !subcategoryIds.includes(id));
          }
        }
      }

      return {
        ...prevForm,
        PCategories: newCategories
      };
    });
  };

  // NEW: Handle main category selection with auto-subcategory selection
  const handleMainCategoryChange = (mainCategoryId, isChecked) => {
    const mainCategory = organizedCategories.find(cat => cat.id === mainCategoryId);
    
    setForm((prevForm) => {
      let newCategories = [...prevForm.PCategories];
      
      if (isChecked) {
        // Add main category
        if (!newCategories.includes(mainCategoryId)) {
          newCategories.push(mainCategoryId);
        }
        // Auto-select all subcategories
        if (mainCategory.subcategories) {
          mainCategory.subcategories.forEach(sub => {
            if (!newCategories.includes(sub.id)) {
              newCategories.push(sub.id);
            }
          });
        }
      } else {
        // Remove main category and all its subcategories
        newCategories = newCategories.filter(id => id !== mainCategoryId);
        if (mainCategory.subcategories) {
          const subcategoryIds = mainCategory.subcategories.map(sub => sub.id);
          newCategories = newCategories.filter(id => !subcategoryIds.includes(id));
        }
      }

      return {
        ...prevForm,
        PCategories: newCategories
      };
    });
  };

  // NEW: Check if all subcategories of a main category are selected
  const areAllSubcategoriesSelected = (mainCategory) => {
    if (!mainCategory.subcategories || mainCategory.subcategories.length === 0) {
      return false;
    }
    
    return mainCategory.subcategories.every(sub => 
      form.PCategories.includes(sub.id)
    );
  };

  // NEW: Check if some subcategories of a main category are selected
  const areSomeSubcategoriesSelected = (mainCategory) => {
    if (!mainCategory.subcategories || mainCategory.subcategories.length === 0) {
      return false;
    }
    
    const selectedCount = mainCategory.subcategories.filter(sub => 
      form.PCategories.includes(sub.id)
    ).length;
    
    return selectedCount > 0 && selectedCount < mainCategory.subcategories.length;
  };

  // --- Product Type/Variant Handlers ---

  const handleTypeChange = (id, field, value) => {
    setForm((prevForm) => ({
      ...prevForm,
      PType: prevForm.PType.map((type) =>
        type.id === id
          ? { 
              ...type, 
              [field]: field === 'PPrice' || field === 'PDiscountPrice' 
                ? Number(value) 
                : value 
            }
          : type
      ),
    }));
  };

  const handleTypeCheckboxChange = (id, field, checked) => {
    setForm((prevForm) => ({
      ...prevForm,
      PType: prevForm.PType.map((type) =>
        type.id === id
          ? { ...type, [field]: checked }
          : type
      ),
    }));
  };

  // --- Stock Management Handlers ---
  const handleStockChange = (typeId, stockId, field, value) => {
    setForm((prevForm) => ({
      ...prevForm,
      PType: prevForm.PType.map((type) =>
        type.id === typeId
          ? {
              ...type,
              PStock: type.PStock.map((stock) =>
                stock.id === stockId
                  ? { 
                      ...stock, 
                      [field]: field === 'Qty' ? Number(value) : value 
                    }
                  : stock
              )
            }
          : type
      ),
    }));
  };

  const addStock = (typeId) => {
    setForm((prevForm) => ({
      ...prevForm,
      PType: prevForm.PType.map((type) =>
        type.id === typeId
          ? {
              ...type,
              PStock: [
                ...type.PStock,
                {
                  id: createId(),
                  Qty: 0,
                  Size: ""
                }
              ]
            }
          : type
      ),
    }));
  };

  const removeStock = (typeId, stockId) => {
    setForm((prevForm) => ({
      ...prevForm,
      PType: prevForm.PType.map((type) =>
        type.id === typeId
          ? {
              ...type,
              PStock: type.PStock.filter((stock) => stock.id !== stockId)
            }
          : type
      ),
    }));
  };

  // --- File Management Handlers ---
  const [typeFiles, setTypeFiles] = useState({});

  const handleTypeFileChange = (typeId, fileType, file, index = null) => {
    setTypeFiles((prevFiles) => {
      const typeFiles = prevFiles[typeId] || { images: Array(MAX_IMAGES).fill(null), video: null };
      
      if (fileType === "image") {
        const newImages = [...typeFiles.images];
        newImages[index] = file;
        return {
          ...prevFiles,
          [typeId]: { ...typeFiles, images: newImages }
        };
      } else if (fileType === "video") {
        return {
          ...prevFiles,
          [typeId]: { ...typeFiles, video: file }
        };
      }
      return prevFiles;
    });
  };

  const removeTypeFile = (typeId, fileType, index = null) => {
    setTypeFiles((prevFiles) => {
      const typeFiles = prevFiles[typeId] || { images: Array(MAX_IMAGES).fill(null), video: null };
      
      if (fileType === "image") {
        const newImages = [...typeFiles.images];
        newImages[index] = null;
        return {
          ...prevFiles,
          [typeId]: { ...typeFiles, images: newImages }
        };
      } else if (fileType === "video") {
        return {
          ...prevFiles,
          [typeId]: { ...typeFiles, video: null }
        };
      }
      return prevFiles;
    });
  };

  const addType = () => {
    setForm((prevForm) => ({
      ...prevForm,
      PType: [
        ...prevForm.PType,
        { 
          id: createId(), 
          PTypeName: "", 
          PPrice: 0, 
          PSoleColor: "",
          PInOffer: false,
          PDiscountPrice: 0,
          PImgUrls: [],
          PVidUrls: [],
          PStock: [
            {
              id: createId(),
              Qty: 0,
              Size: ""
            }
          ]
        },
      ],
    }));
  };

  const removeType = (id) => {
    setForm((prevForm) => ({
      ...prevForm,
      PType: prevForm.PType.filter((type) => type.id !== id),
    }));
    // Also remove any associated files
    setTypeFiles((prevFiles) => {
      const newFiles = { ...prevFiles };
      delete newFiles[id];
      return newFiles;
    });
  };

  // --- Submission Handler ---

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (form.PType.length === 0) {
      alert("Please add at least one Product Type/Variant.");
      setLoading(false);
      return;
    }

    if (form.PCategories.length === 0) {
      alert("Please select at least one category.");
      setLoading(false);
      return;
    }

    try {
      // Prepare Firestore Batch
      const batch = writeBatch(db);
      const productRef = doc(collection(db, "products"));

      // NEW: Auto-assign order number
      const finalOrderNumber = form.order > 0 ? form.order : nextOrderNumber;

      // Upload media for each variant and prepare the final form data
      const finalForm = {
        ...form,
        PDateAdded: new Date().toISOString(),
        // NEW: Auto-assigned order field
        order: finalOrderNumber,
        // NEW: hasActiveOffer is automatically calculated and included
        PType: await Promise.all(
          form.PType.map(async (type) => {
            const typeId = type.id;
            const files = typeFiles[typeId] || { images: Array(MAX_IMAGES).fill(null), video: null };

            // Upload images for this variant
            const imgUploadPromises = files.images
              .filter((file) => file !== null)
              .map((file) => uploadFile(file, "product_images"));
            const imgUrls = await Promise.all(imgUploadPromises);

            // Upload video for this variant
            let vidUrls = [];
            if (files.video) {
              const videoURL = await uploadFile(files.video, "product_videos");
              if (videoURL) {
                vidUrls = [videoURL];
              }
            }

            // Return the variant with uploaded URLs (no file objects)
            return {
              ...type,
              PImgUrls: imgUrls,
              PVidUrls: vidUrls,
            };
          })
        ),
      };

      // A. Set the new Product Document
      batch.set(productRef, finalForm);

      // 3. Commit the Batch
      await batch.commit();

      alert(`✅ Product added successfully with order number ${finalOrderNumber}!`);
      navigate("/admin/products");
    } catch (err) {
      console.error(err);
      alert("Error adding product or updating categories. Check console for details.");
    }

    setLoading(false);
  };

  // NEW: Function to render category with subcategories
  const renderCategoryWithSubcategories = (category) => {
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const allSubsSelected = areAllSubcategoriesSelected(category);
    const someSubsSelected = areSomeSubcategoriesSelected(category);

    return (
      <div key={category.id} className="mb-2">
        {/* Main Category */}
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-1">
            <input
              type="checkbox"
              id={`category-${category.id}`}
              checked={form.PCategories.includes(category.id) || allSubsSelected}
              ref={(input) => {
                if (input) {
                  input.indeterminate = someSubsSelected;
                }
              }}
              onChange={(e) => handleMainCategoryChange(category.id, e.target.checked)}
              className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label 
              htmlFor={`category-${category.id}`}
              className="ml-3 text-sm font-medium text-gray-700 cursor-pointer hover:text-gray-900 flex items-center gap-2"
            >
              {category.CategoryName}
              {hasSubcategories && (
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  {category.subcategories.length} sub{category.subcategories.length !== 1 ? 's' : ''}
                </span>
              )}
            </label>
          </div>
          
          {hasSubcategories && (
            <button
              type="button"
              onClick={() => toggleCategoryExpansion(category.id)}
              className="text-gray-500 hover:text-gray-700 transition-transform ml-2"
            >
              <svg 
                className={`w-4 h-4 transform ${isExpanded ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>

        {/* Subcategories */}
        {hasSubcategories && isExpanded && (
          <div className="ml-6 mt-2 space-y-2 border-l-2 border-gray-200 pl-4">
            {category.subcategories.map((subcategory) => (
              <div key={subcategory.id} className="flex items-center">
                <input
                  type="checkbox"
                  id={`subcategory-${subcategory.id}`}
                  checked={form.PCategories.includes(subcategory.id)}
                  onChange={(e) => handleCategoryChange(subcategory.id, e.target.checked, true)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label 
                  htmlFor={`subcategory-${subcategory.id}`}
                  className="ml-3 text-sm text-gray-600 cursor-pointer hover:text-gray-800 flex items-center gap-2"
                >
                  {subcategory.CategoryName}
                  <span className="text-xs text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                    Subcategory
                  </span>
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Markdown guide content (simplified)
  const markdownGuide = `# Formatting Guide

## Text Formatting
- **Bold**: Select text and click **B** or type **text**
- *Italic*: Select text and click *I* or type *text*
- <u>Underline</u>: Select text and click <u>U</u>

## Headers
# Header 1
## Header 2  
### Header 3

## Lists
- Bullet points: Click "• List"
- Numbered lists: Click "1. List"`;

  return (
    <div className="p-6">
      <div className="mb-8 flex justify-between items-center">
        " "
      </div>
      <h2 className="text-2xl font-bold mb-6 text-purple-800">Add New Product</h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6 max-w-4xl">
        {/* --- Primary Details --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              name="PName"
              placeholder="Product Name (e.g., Ultra-Boost Shoe)"
              value={form.PName}
              onChange={handleChange}
              required
              className="border p-2 rounded w-full"
            />
          </div>
          <div>
            <input
              type="text"
              name="PCode"
              placeholder="Product Code (SKU)"
              value={form.PCode}
              onChange={handleChange}
              className="border p-2 rounded w-full"
            />
          </div>
          {/* NEW: Order Number Input */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Order Number
              <span className="text-xs text-gray-500 ml-1">(Optional)</span>
            </label>
            <input
              type="number"
              name="order"
              placeholder={`Auto: ${nextOrderNumber}`}
              value={form.order}
              onChange={handleChange}
              min="1"
              className="border p-2 rounded w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              {form.order > 0 ? `Manual: ${form.order}` : `Auto-assigned: ${nextOrderNumber}`}
            </p>
          </div>
        </div>

        {/* Simple Description */}
        <div>
          <label className="block font-medium mb-2">Short Description</label>
          <textarea
            name="PDesc"
            placeholder="Brief product description for listings and previews..."
            value={form.PDesc}
            onChange={handleChange}
            className="border p-2 rounded w-full h-32"
            maxLength={200}
          />
          <p className="text-sm text-gray-500 mt-1">
            {form.PDesc.length}/200 characters - Keep it concise for product cards
          </p>
        </div>

        {/* NEW: Detailed Description with Custom Rich Text Editor */}
        <div>
          <label className="block font-medium mb-2">
            Detailed Description 
            <span className="text-sm font-normal text-gray-600 ml-2">
              (Rich text editor with formatting tools)
            </span>
          </label>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Editor Side */}
            <div>
              <RichTextEditor
                value={form.PDetailedDesc}
                onChange={handleDetailedDescChange}
                placeholder="Write detailed product description... Use the toolbar above for formatting."
              />
            </div>
            
            {/* Preview Side */}
            <div>
              <label className="block font-medium mb-2 text-gray-700">Live Preview</label>
              <MarkdownPreview content={form.PDetailedDesc} />
            </div>
          </div>
          
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <details className="cursor-pointer">
              <summary className="font-medium text-blue-800">📝 Formatting Guide</summary>
              <div className="mt-2 text-sm text-blue-700">
                <MarkdownPreview content={markdownGuide} />
              </div>
            </details>
          </div>
        </div>

        {/* --- Active Offer Indicator --- */}
        <div className="p-4 border rounded bg-green-50">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${form.hasActiveOffer ? 'bg-green-500' : 'bg-gray-300'}`}></div>
            <span className="font-medium">
              {form.hasActiveOffer ? '✅ This product has active offers' : '❌ No active offers'}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            This status is automatically updated when you mark variants as "on offer"
          </p>
        </div>

        {/* --- Updated Categories Selection with Hierarchy --- */}
        <div>
          <label className="block font-medium mb-3">Categories (Select one or more)</label>
          <div className="border p-4 rounded bg-white max-h-96 overflow-y-auto">
            <div className="space-y-2">
              {organizedCategories.map((category) => 
                renderCategoryWithSubcategories(category)
              )}
              {organizedCategories.length === 0 && (
                <p className="text-gray-500 text-sm">No categories available. Please add categories first.</p>
              )}
            </div>
          </div>
          {form.PCategories.length > 0 && (
            <p className="text-sm text-green-600 mt-2">
              {form.PCategories.length} categor{form.PCategories.length === 1 ? 'y' : 'ies'} selected
            </p>
          )}
        </div>

        {/* --- Product Types/Variants --- */}
        <div className="border p-4 rounded bg-gray-50">
          <h3 className="font-semibold mb-3">Product Types/Variants ({form.PType.length})</h3>
          {form.PType.map((type, typeIndex) => {
            const typeFilesData = typeFiles[type.id] || { images: Array(MAX_IMAGES).fill(null), video: null };
            const totalStock = type.PStock.reduce((sum, stock) => sum + stock.Qty, 0);
            
            return (
              <div key={type.id} className="mb-6 border-b pb-4 last:border-b-0">
                {/* Variant Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {/* Variant Name */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Variant Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Red/Large"
                      value={type.PTypeName}
                      onChange={(e) => handleTypeChange(type.id, "PTypeName", e.target.value)}
                      required
                      className="border p-2 rounded w-full"
                    />
                  </div>

                  {/* Price in Rupees */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Price (PKR)</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={type.PPrice}
                      onChange={(e) => handleTypeChange(type.id, "PPrice", e.target.value)}
                      required
                      min="0"
                      step="1"
                      className="border p-2 rounded w-full"
                    />
                  </div>

                  {/* Total Stock Display */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Total Stock</label>
                    <div className="border p-2 rounded bg-white">
                      <span className={`font-semibold ${totalStock === 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {totalStock} units
                      </span>
                    </div>
                  </div>

                  {/* Sole Color */}
                  <div>
                    <label className="block text-sm font-medium mb-1">Sole Color</label>
                    <input
                      type="text"
                      placeholder="e.g., Black, White"
                      value={type.PSoleColor}
                      onChange={(e) => handleTypeChange(type.id, "PSoleColor", e.target.value)}
                      className="border p-2 rounded w-full"
                    />
                  </div>
                </div>

                {/* Stock Management */}
                <div className="ml-4 mb-4 p-4 border rounded bg-blue-50">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium">Stock Management</label>
                    <button
                      type="button"
                      onClick={() => addStock(type.id)}
                      className="bg-green-500 text-white px-3 py-1 rounded text-sm"
                    >
                      + Add Stock
                    </button>
                  </div>
                  
                  {type.PStock.map((stock, stockIndex) => (
                    <div key={stock.id} className="flex gap-3 items-end mb-2">
                      <div className="flex-1">
                        <label className="block text-xs font-medium mb-1">Size</label>
                        <input
                          type="text"
                          placeholder="e.g., S, M, L, 40, 42"
                          value={stock.Size}
                          onChange={(e) => handleStockChange(type.id, stock.id, "Size", e.target.value)}
                          className="border p-2 rounded w-full"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-medium mb-1">Quantity</label>
                        <input
                          type="number"
                          placeholder="0"
                          value={stock.Qty}
                          onChange={(e) => handleStockChange(type.id, stock.id, "Qty", e.target.value)}
                          min="0"
                          className="border p-2 rounded w-full"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeStock(type.id, stock.id)}
                        disabled={type.PStock.length === 1}
                        className="bg-red-500 text-white p-2 rounded text-sm disabled:opacity-50 mb-1"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                {/* Offer Section */}
                <div className="ml-4 mb-4 p-4 border rounded bg-yellow-50">
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="checkbox"
                      id={`offer-${type.id}`}
                      checked={type.PInOffer}
                      onChange={(e) => handleTypeCheckboxChange(type.id, "PInOffer", e.target.checked)}
                      className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <label 
                      htmlFor={`offer-${type.id}`}
                      className="text-sm font-medium text-gray-700 cursor-pointer"
                    >
                      This variant is on offer
                    </label>
                  </div>
                  
                  {type.PInOffer && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Discounted Price (PKR)</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={type.PDiscountPrice}
                        onChange={(e) => handleTypeChange(type.id, "PDiscountPrice", e.target.value)}
                        min="0"
                        step="1"
                        className="border p-2 rounded w-full md:w-48"
                      />
                      {type.PDiscountPrice > 0 && type.PPrice > 0 && (
                        <p className="text-sm text-green-600 mt-1">
                          Discount: PKR{(type.PPrice - type.PDiscountPrice).toFixed(2)} 
                          ({(100 - (type.PDiscountPrice / type.PPrice) * 100).toFixed(1)}% off)
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Variant Images */}
                <div className="ml-4 mb-3">
                  <label className="block font-medium mb-2 text-sm">
                    Variant Images (Up to {MAX_IMAGES})
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {typeFilesData.images.map((file, imgIndex) => (
                      <div
                        key={imgIndex}
                        className="relative border border-dashed border-gray-400 p-2 rounded w-24 h-24 flex items-center justify-center text-center"
                      >
                        {file ? (
                          <>
                            <span className="text-xs break-all truncate">
                              {file.name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeTypeFile(type.id, "image", imgIndex)}
                              className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center -mt-2 -mr-2"
                            >
                              X
                            </button>
                          </>
                        ) : (
                          <label className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                            Image {imgIndex + 1}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleTypeFileChange(type.id, "image", e.target.files[0], imgIndex)}
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Variant Video */}
                <div className="ml-4">
                  <label className="block font-medium mb-2 text-sm">Variant Video</label>
                  <div className="relative border border-dashed border-gray-400 p-2 rounded w-40 h-28 flex items-center justify-center text-center">
                    {typeFilesData.video ? (
                      <>
                        <span className="text-xs break-all truncate">
                          {typeFilesData.video.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeTypeFile(type.id, "video")}
                          className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center -mt-2 -mr-2"
                        >
                          X
                        </button>
                      </>
                    ) : (
                      <label className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                        Upload Video
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => handleTypeFileChange(type.id, "video", e.target.files[0])}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Remove Variant Button */}
                <div className="ml-4 mt-3">
                  <button
                    type="button"
                    onClick={() => removeType(type.id)}
                    disabled={form.PType.length === 1}
                    className="bg-red-500 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                  >
                    Remove Variant
                  </button>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addType}
            className="bg-blue-500 text-white px-4 py-2 rounded mt-2"
          >
            + Add Variant
          </button>
        </div>

        {/* --- Order Information Display --- */}
        <div className="p-4 border rounded bg-purple-50">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="font-medium text-purple-800">
              Order Information
            </span>
          </div>
          <p className="text-sm text-purple-700 mt-1">
            {form.order > 0 
              ? `This product will be assigned order number: ${form.order}`
              : `This product will be automatically assigned order number: ${nextOrderNumber}`
            }
          </p>
          <p className="text-xs text-purple-600 mt-1">
            Products are displayed in ascending order by this number. Leave empty for auto-assignment.
          </p>
        </div>

        {/* --- Submit Button --- */}
        <button
          type="submit"
          disabled={loading}
          className="bg-purple-700 text-white px-4 py-3 rounded-lg hover:bg-purple-800 transition disabled:bg-gray-400 mt-4"
        >
          {loading ? "Uploading & Saving..." : "Add Product"}
        </button>
      </form>
    </div>
  );
}